import { Theme } from '@mui/material/styles'

import {
  layout,
  semanticTokens,
  typography as typeTokens,
} from '../tokens.generated'
import { switchPaintInset } from './Switch'

// ----------------------------------------------------------------------
// The text around a control.
//
// A form label is 12px semibold in the secondary text role, whether it sits
// above a field (`InputLabel`, which extends it) or heads a radio group. It
// used to be the disabled text colour, which is what a label is not.
//
// A control label (the word beside a checkbox or a switch) is body size, one
// gap from the control, and starts at the same left edge as everything else.
// MUI pulls it 11px left to line the checkbox glyph up with text above it; on
// a form built from static labels there is nothing to line up with, and the
// negative margin put the control outside its card.
//
// Two things about that gap and that weight are decisions rather than
// defaults, and both are stated at the rules below: the gap is measured from
// the edge the control *paints* rather than the edge of its hit box, and a
// tick's word is set at body weight while a switch's stays semibold because
// one is a sentence you are agreeing to and the other is the title of a row.
//
// Helper and error text is caption size, one step under the field, and flush
// with it rather than indented to MUI's notch inset.
// ----------------------------------------------------------------------

export default function ControlLabel(theme: Theme) {
  const semantics = semanticTokens[theme.palette.mode]

  return {
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          'marginLeft': 0,
          'marginRight': 0,
          // **The gap is optical, so it is stated as what is left of it.**
          //
          // A control does not begin where its box begins. A checkbox paints
          // `tickBoxInset` inside its own (the glyph's 3px margin on the 24
          // grid plus the control's padding), and a medium switch paints the
          // same distance in, because that is where its track starts. So the
          // row adds only the remainder of `controlGap`, and the distance from
          // the square an operator sees to the first letter is the 8 the
          // system publishes rather than the 21 three files used to add up to.
          'gap': layout.controlGap - layout.tickBoxInset,
          // A small switch paints less far in than the rest, so it pays the
          // difference back. Computed from the switch's own box rather than
          // typed, so a change to the track geometry moves this with it.
          '& .MuiSwitch-sizeSmall': {
            marginRight: layout.tickBoxInset - switchPaintInset('small'),
          },
          // **A tick's word is the sentence you are ticking, not a heading.**
          //
          // A switch's word is the title of a row that usually carries a
          // description under it, so it stays semibold. Setting a checkbox's
          // word to the same weight made every form read as a list of
          // headings with nothing under them. The label span is the control's
          // immediate next sibling inside `FormControlLabel`, which is what
          // lets this be a sibling combinator rather than a `:has()`, and a
          // combinator is two classes, so it beats the `label` slot below
          // without out-specifying anything by hand.
          '& .MuiCheckbox-root + .MuiFormControlLabel-label, & .MuiRadio-root + .MuiFormControlLabel-label':
            {
              'fontWeight': typeTokens.console.body.weight,
              // **A tick's word is centred by its capitals, not its line box.**
              //
              // MUI centres the label's line box on the control, and a line
              // box keeps room for descenders the eye does not read. A reader
              // places a label by its capital band, which sits above the line
              // box's middle, so the box beside every labelled checkbox and
              // radio in the console read as sitting low: `Show empty
              // sessions` was a pixel and a half out in the report, while the
              // two boxes were centred to a hundredth of a pixel. Relative, so
              // it moves the ink and nothing else: the row's height, the gap
              // and the hit target stay where they are. Checkbox and radio
              // only, because that is what was measured; a switch's word is a
              // row title beside a track, not a word beside a square.
              'position': 'relative',
              'top': layout.tickLabelDrop,
              // **A mark in the label is not a word, so it is not lowered.**
              // The drop exists because a reader places a word by its capital
              // band; a flag or a glyph is placed by its box, and the drop
              // took it a pixel below the square it sits beside. Measured on
              // the vehicle's Booking Locations tab: the Estonian flag's centre
              // 1px under the checkbox's, its foot level with the box's foot
              // and its head 2px inside. So the mark is lifted back by exactly
              // the drop, and only inside a tick's label.
              '& svg, & [role="img"]': {
                position: 'relative',
                top: -layout.tickLabelDrop,
              },
            },
        },
        label: {
          fontSize: theme.typography.pxToRem(typeTokens.console.body.size),
          fontWeight: typeTokens.weights.semibold,
          lineHeight: typeTokens.console.body.lineHeight,
        },
      },
    },
    // A run of tickable rows is one question with several answers, not several
    // fields, so it takes its own rhythm. `FormGroup` is MUI's only container
    // for such a run and `RHFMultiCheckbox` renders one; everything else that
    // stacks ticks by hand uses `TickGroup`, which computes the identical
    // expression from the identical token.
    //
    // **And it is an expression for the same reason the row gap above is
    // one.** `tickRowGap` is measured between the squares an operator sees,
    // and a checkbox paints `tickBoxInset` inside its own box on each of its
    // four sides, so a gap declared as the token drew the token plus both
    // insets, 26px of white where 12 was published. The two consumers subtract
    // it in the same shape so the group and the `FormGroup` cannot fork.
    MuiFormGroup: {
      styleOverrides: {
        root: {
          gap: `${layout.tickRowGap - 2 * layout.tickBoxInset}px`,
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          'marginTop': theme.spacing(0.5),
          'marginInline': 0,
          'fontSize': theme.typography.pxToRem(typeTokens.console.caption.size),
          'lineHeight': typeTokens.console.caption.lineHeight,
          // The error text role, not the error fill: the fill is a meter
          // colour and does not clear 4.5:1 on a surface.
          '&.Mui-error': {
            color: semantics.error.text,
          },
        },
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          'color': theme.palette.text.secondary,
          'fontSize': theme.typography.pxToRem(typeTokens.console.caption.size),
          'fontWeight': typeTokens.weights.semibold,
          '&.Mui-focused': {
            color: theme.palette.text.secondary,
          },
          '&.Mui-error': {
            color: semantics.error.text,
          },
        },
      },
    },
  }
}
